import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import RichText from '@eneco/components/RichText/RichText';
import { Bleed, Box, Divider, Expandable, Grid, Heading, Stack, Text, TextLink } from '@eneco/sparky';
import { ChevronRightIcon } from '@eneco/sparky/icons';
import type { HeadingProps } from '@eneco/sparky/types';

import type { ContentCategoriesContent, ContentCategoriesItem } from './content-categories-content.types';

/**
 * `ContentCategories` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/content/src/ContentCategories/ContentCategories.tsx`: an optional
 * title, then the categories split over two columns, each an `Expandable` with an optional rich-text
 * description and a list of links. Drops the legacy `wrap()`/Experience Editor `editable` checks — this
 * lane never runs inside the Experience Editor — and the legacy `useLinkComponent()` in favour of
 * `linkWithUrl()` plus a plain `TextLink`, matching `LinkCard.tsx`.
 *
 * The description's `Text` renders even when there is no description. The legacy component tests the
 * Sitecore field object, which is always present, so it renders an empty `Text` that still takes a
 * `Stack` gap above the links; Sanity omits the field entirely. Rendering the slot unconditionally keeps
 * that spacing identical on both lanes.
 */
const ContentCategories: FC = () => {
  const { adapter } = useCms();
  const content = useContent<ContentCategoriesContent>();
  const headingLevel = (useSetting('headingLevel') as HeadingProps['as']) ?? 'h2';

  const items = content.items ?? [];
  if (items.length === 0) return null;

  const nestedTitleLevel = `h${Math.min(6, Number(headingLevel.charAt(1)) + 1)}` as HeadingProps['as'];

  const generateExpandable = (item: ContentCategoriesItem, fallbackKey: number) => {
    const descriptionHtml = adapter.toRichText(item.description);
    const links = (item.items ?? []).filter(linkItem => linkWithUrl(linkItem.link)?.text);

    return (
      <Grid.Item key={item.id ?? item._key ?? fallbackKey}>
        <Box paddingX={{ initial: '0', md: '6' }}>
          <Expandable>
            {item.title && (
              <Expandable.Trigger label={item.title}>
                <Box paddingY={{ initial: '5', md: '6' }}>
                  <Heading as={nestedTitleLevel} size="S" color="inherit">
                    {item.title}
                  </Heading>
                </Box>
              </Expandable.Trigger>
            )}
            <Expandable.Content>
              <Box paddingBottom="8">
                <Stack gap="6">
                  <Text>{descriptionHtml ? <RichText html={descriptionHtml} /> : null}</Text>
                  {item.items && item.items.length > 0 ? (
                    <Box paddingLeft="1" paddingTop="3">
                      <Stack gap="5" as="ul">
                        {links.map(linkItem => (
                          <Stack.Item key={linkItem.id ?? linkItem._key} as="li">
                            <TextLink
                              href={linkItem.link?.href}
                              target={linkItem.link?.target}
                              emphasis="medium"
                              leftIcon={<ChevronRightIcon />}>
                              {linkItem.link?.text}
                            </TextLink>
                          </Stack.Item>
                        ))}
                      </Stack>
                    </Box>
                  ) : null}
                </Stack>
              </Box>
            </Expandable.Content>
          </Expandable>
        </Box>
        <Bleed horizontal={{ initial: 'gridGutter', md: '0' }}>
          <Divider />
        </Bleed>
      </Grid.Item>
    );
  };

  const itemsPerColumn = Math.ceil(items.length / 2);

  return (
    <>
      {content.title && (
        <Box>
          <Heading as={headingLevel} size="M">
            {content.title}
          </Heading>
        </Box>
      )}
      <Grid columns={{ initial: '1', md: '2' }} columnGap="6">
        <Stack>{items.slice(0, itemsPerColumn).map((item, i) => generateExpandable(item, i))}</Stack>
        <Stack>{items.slice(itemsPerColumn).map((item, i) => generateExpandable(item, i))}</Stack>
      </Grid>
    </>
  );
};

export default ContentCategories;
